import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Button, Skeleton, Tooltip, Alert } from 'antd';
import { FilterOutlined } from '@ant-design/icons';

/* import { getControlsArea } from '../../API/controls';
import { getControls } from '../../API/controls'; */
import { getAssetsFilter } from '../../API/dashboard';

import { 
  getComplianceLevel, 
  getDashboardGeneral, 
  getEvaluationStatus, 
  getGapStatus, 
  getTaskStatus,
  getControlStatus
} from '../../API/dashboard';

import assetCard from './assets/asset.png';
import dependenciesCard from './assets/dependencies.png';
import controlsCard from './assets/controls.png';
import gapsCard from './assets/gaps.png'

import ControlsByPriorityChart from './components/DashboardCharts/ControlsByPriorityChart';
import ControlsByTypeChart from './components/DashboardCharts/ControlsByTypeChart';
import ConditionTasksChart from './components/DashboardCharts/ConditionTasksChart';
import TasksStatusChart from './components/DashboardCharts/TasksStatusChart';
import EvaluationsChart from './components/DashboardCharts/EvaluationsChart';
import MaturityChart from './components/DashboardCharts/MaturityChart';
import GapStatusChart from './components/DashboardCharts/GapStatusChart';
import DashboardFilter from './components/DashboardFilter';

import WithPermission from "../Common/WithPermission/WithPermission";
import PageTitle from '../Common/PageTitle/PageTitle';
import { useAuth } from "../Context/AuthContext";

import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  
  // Permisos
  const { hasPermission, userData } = useAuth();
  const isSuperuser = userData?.is_superuser === true;
  const hasAnyRole = userData?.roles?.length > 0;
  const canAssetsRead = userData ? hasPermission("assets", "read") : false;
  const canDependenciesRead = userData ? hasPermission("dependencies", "read") : false;
  const canControlsRead = userData ? hasPermission("controls", "read") : false;
  const canEvaluationRead = userData ? hasPermission("evaluation", "read") : false;
  const canGapsRead = userData ? hasPermission("gaps", "read") : false;
  const canTasksRead = userData ? hasPermission("tasks", "read") : false;

  // Estados de UI
  const [openFilter, setOpenFilter] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Estados de filtros
  const [appliedFilters, setAppliedFilters] = useState({});
  
  // Estados de datos
  const [assets, setAssets] = useState([]);
  const [controls, setControls] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [maturityLevel, setMaturityLevel] = useState([]);
  const [dashboardGeneral, setDashboardGeneral] = useState({});
  //const [areas, setAreas] = useState([]);

  // Función centralizada para cargar datos del dashboard
  const fetchDashboardData = async (filters = {}) => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        getControlStatus(filters),
        getAssetsFilter(),
        getGapStatus(filters),
        getEvaluationStatus(filters),
        getComplianceLevel(filters),
        getDashboardGeneral(filters),
        getTaskStatus(filters),
        //getControlsArea("dashboards"),
      ]);

      // Procesar cada resultado individualmente
      const [
        controlsResult,
        assetsResult,
        gapsResult,
        evaluationsResult,
        maturityResult,
        dashboardResult,
        tasksResult,
        //areasResult
      ] = results;

      // Actualizar estados solo si la promesa fue exitosa
      if (controlsResult.status === 'fulfilled') {
        setControls(controlsResult.value);
      } else {
        console.error("Error al obtener controles:", controlsResult.reason);
        setControls([]);
      }

      if (assetsResult.status === 'fulfilled') {
        setAssets(assetsResult.value);
      } else {
        console.error("Error al obtener assets:", assetsResult.reason);
        setAssets([]);
      }

      if (gapsResult.status === 'fulfilled') {
        setGaps(gapsResult.value);
      } else {
        console.error("Error al obtener gaps:", gapsResult.reason);
        setGaps([]);
      }

      if (evaluationsResult.status === 'fulfilled') {
        setEvaluations(evaluationsResult.value);
      } else {
        console.error("Error al obtener evaluaciones:", evaluationsResult.reason);
        setEvaluations([]);
      }

      if (maturityResult.status === 'fulfilled') {
        setMaturityLevel(maturityResult.value);
      } else {
        console.error("Error al obtener nivel de madurez:", maturityResult.reason);
        setMaturityLevel([]);
      }

      if (dashboardResult.status === 'fulfilled') {
        setDashboardGeneral(dashboardResult.value);
      } else {
        console.error("Error al obtener datos generales:", dashboardResult.reason);
        setDashboardGeneral({});
      }

      if (tasksResult.status === 'fulfilled') {
        setTasks(tasksResult.value);
      } else {
        console.error("Error al obtener tareas:", tasksResult.reason);
        setTasks([]);
      }

      /* if (areasResult.status === 'fulfilled') {
        setAreas(areasResult.value);
      } else {
        console.error("Error al obtener áreas:", areasResult.reason);
        setAreas([]);
      } */

    } catch (error) {
      console.error("Error inesperado al obtener datos del dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  // Carga inicial sin filtros
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  // Función para agrupar controles por una propiedad específica
  const groupControlsBy = (controls, key) => {
    const grouped = {};
  
    controls.forEach(control => {
      const groupKey = control[key]?.name;
      if (groupKey) {
        grouped[groupKey] = (grouped[groupKey] || 0) + 1;
      }
    });
    
    return Object.entries(grouped).map(([name, value]) => ({
      name,
      value,
    }));
  };

  const showDrawer = () => setOpenFilter(true);

  const handleApplyFilters = async (filters) => {
    console.log('Filtros aplicados:', filters);
    setAppliedFilters(filters);
    await fetchDashboardData(filters);
    setOpenFilter(false);
  };

  // Función helper para construir URLs con filtros
 /*  const buildNavigationUrl = (basePath) => {
    const params = new URLSearchParams();
    
    // Si hay activos seleccionados, los agregamos como parámetros
    if (appliedFilters.asset && appliedFilters.asset.length > 0) {
      // Convertir códigos de activos a nombres para el filtro
      const assetNames = appliedFilters.asset
        .map(code => assets.find(a => a.code === code)?.name)
        .filter(Boolean);
      
      if (assetNames.length > 0) {
        params.append('assets', assetNames.join(','));
      }
    }
    
    // Si hay áreas seleccionadas, las agregamos
    if (appliedFilters.area && appliedFilters.area.length > 0) {
      // Extraer todas las filter_areas únicas de los assets
      const allFilterAreas = assets.reduce((acc, asset) => {
        if (asset.filter_areas) {
          asset.filter_areas.forEach(area => {
            if (!acc.find(a => a.id === area.id)) {
              acc.push(area);
            }
          });
        }
        return acc;
      }, []);

      const areaNames = appliedFilters.area
        .map(id => allFilterAreas.find(a => a.id === id)?.name)
        .filter(Boolean);
      
      if (areaNames.length > 0) {
        params.append('areas', areaNames.join(','));
      }
    }
    
    const queryString = params.toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
  }; */
  // Función helper para construir URLs con filtros
const buildNavigationUrl = (basePath) => {
  const params = new URLSearchParams();
  
  // ESCENARIO 1: Usuario HA FILTRADO
  if (Object.keys(appliedFilters).length > 0) {
    // Usar la lógica original que ya funciona
    if (appliedFilters.asset && appliedFilters.asset.length > 0) {
      const assetNames = appliedFilters.asset
        .map(code => assets.find(a => a.code === code)?.name)
        .filter(Boolean);
      
      if (assetNames.length > 0) {
        params.append('assets', assetNames.join(','));
      }
    }
    
    if (appliedFilters.area && appliedFilters.area.length > 0) {
      const allFilterAreas = assets.reduce((acc, asset) => {
        if (asset.filter_areas) {
          asset.filter_areas.forEach(area => {
            if (!acc.find(a => a.id === area.id)) {
              acc.push(area);
            }
          });
        }
        return acc;
      }, []);

      const areaNames = appliedFilters.area
        .map(id => allFilterAreas.find(a => a.id === id)?.name)
        .filter(Boolean);
      
      if (areaNames.length > 0) {
        params.append('areas', areaNames.join(','));
      }
    }
  }
  // ESCENARIO 2: Usuario NO HA FILTRADO (appliedFilters vacío)
  else {
    // Solo aplicar para rutas específicas
    if (basePath === '/assets') {
      // Para Activos: pasar TODOS los assets
      if (dashboardGeneral?.assets?.items) {
        const assetNames = dashboardGeneral.assets.items
          .map(item => item.name)
          .filter(Boolean);
        
        if (assetNames.length > 0) {
          params.append('assets', assetNames.join(','));
        }
      }
    }
    else if (basePath === '/dependencies') {
      // Para Dependencias: pasar TODAS las dependencias
      if (dashboardGeneral?.dependencies?.items) {
        const dependencyNames = dashboardGeneral.dependencies.items
          .map(item => item.name)
          .filter(Boolean);
        
        if (dependencyNames.length > 0) {
          params.append('dependencies', dependencyNames.join(','));
        }
      }
    }
    // Para /evaluation y /gaps: NO pasar parámetros cuando no hay filtros
  }
  
  const queryString = params.toString();
    console.log('buildNavigationUrl -', {
      basePath,
      appliedFilters,
      hasFilters: Object.keys(appliedFilters).length > 0,
      queryString
    });
    return queryString ? `${basePath}?${queryString}` : basePath;
  };

console.log("dashboardGeneral", dashboardGeneral)
  
  return (
    <WithPermission
      isSuperuser={isSuperuser}
      hasAnyRole={hasAnyRole}
      requiredPermission="read"
      currentPermission={hasAnyRole}
      redirectTo="/home"
    >
      <div>
        <div className="button-container">
          <Row justify="start">
            <Col span={23}>
              <PageTitle title="Tablero de Gestión de Controles Asignados" icon="dashboard" />
            </Col>
            <Col span={1} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title="Filtro" placement="left">
                <div className="settings-icon-container" onClick={showDrawer}>
                  <FilterOutlined className="settings-icon" />
                </div>
              </Tooltip>
            </Col>
          </Row>
        </div>

        <DashboardFilter
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          onApply={handleApplyFilters}
          assets={assets}
          currentFilters={appliedFilters}
        />

        {/* Primera fila: 4 cards a lo ancho */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={6}>
            <Card className="shortcut-card">
              <Skeleton loading={loading} active>
                <Row>
                  <Col span={8} className="card-green-background">
                    <img src={assetCard} className="card-img" alt="Activos" />
                  </Col>
                    <Col
                      span={16}
                      className={`card-text-content ${
                        (isSuperuser || (hasAnyRole && canAssetsRead)) ||
                        dashboardGeneral?.assets?.count > 0
                          ? ''
                          : 'disabled'
                      }`}
                    >
                    <div className="card-header">Activos</div>
                    <div className="card-value">{dashboardGeneral?.assets?.count}</div>
                    <Button 
                      className="custom-button" 
                      disabled={!(dashboardGeneral?.assets?.count > 0)}
                      style={{ alignSelf: 'flex-end' }}
                      onClick={() => navigate(buildNavigationUrl('/assets'))}
                    >
                      Ir a Activos
                    </Button>
                  </Col>
                </Row>
              </Skeleton>
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card className="shortcut-card">
              <Skeleton loading={loading} active>
                <Row>
                  <Col span={8} className="card-green-background">
                    <img src={dependenciesCard} className="card-img" alt="Dependencias" />
                  </Col>
                  <Col
                    span={16}
                    className={`card-text-content ${
                      (isSuperuser || (hasAnyRole && canAssetsRead)) ||
                      dashboardGeneral?.dependencies?.count > 0
                        ? ''
                        : 'disabled'
                    }`}
                  >
                    <div className="card-header">Dependencias</div>
                    <div className="card-value">{dashboardGeneral?.dependencies?.count}</div>
                    <Button 
                      className="custom-button"
                      disabled={!dashboardGeneral?.dependencies?.count}
                      style={{ alignSelf: 'flex-end' }}
                      onClick={() => navigate(buildNavigationUrl('/dependencies'))}
                    >
                      Ir a Dependencias
                    </Button>
                  </Col>
                </Row>
              </Skeleton>
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card className="shortcut-card">
              <Skeleton loading={loading} active>
                <Row>
                  <Col span={8} className="card-green-background">
                    <img src={controlsCard} className="card-img" alt="Controles" />
                  </Col>
                  <Col span={16} className={`card-text-content ${(isSuperuser || (hasAnyRole && canEvaluationRead)) ? '' : 'disabled'}`}>
                    <div className="card-header">Controles Asignados</div>
                    <div className="card-value">{dashboardGeneral?.controls}</div>
                    <Button 
                      className="custom-button"
                      disabled={!dashboardGeneral?.controls}
                      style={{ alignSelf: 'flex-end' }}
                      onClick={() => navigate(buildNavigationUrl('/evaluation'))}
                    >
                      Ir a Evaluación
                    </Button>
                  </Col>
                </Row>
              </Skeleton>
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card className="shortcut-card">
              <Skeleton loading={loading} active>
                <Row>
                  <Col span={8} className="card-green-background">
                    <img src={gapsCard} className="card-img" alt="GAPs" />
                  </Col>
                  <Col span={16} className={`card-text-content ${(isSuperuser || (hasAnyRole && canGapsRead)) ? '' : 'disabled'}`}>
                    <div className="card-header">GAPs</div>
                    <div className="card-value">{dashboardGeneral?.gaps}</div>
                    <Button 
                      className="custom-button" 
                      disabled={!dashboardGeneral?.gaps}
                      style={{ alignSelf: 'flex-end' }}
                      onClick={() => navigate(buildNavigationUrl('/gaps'))}
                    >
                      Ir a Gaps
                    </Button>
                  </Col>
                </Row>
              </Skeleton>
            </Card>
          </Col>
        </Row>

        {/* Segunda fila: 2 cards para gráficos */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Row gutter={[16, 16]} align="stretch">
              <Col xs={24} md={8}>
                <div style={{ height: '100%' }}>
                  <Skeleton loading={loading} paragraph={{ rows: 3, width: '100%' }} active>
                    <Card className="custom-card-title" title="Nivel de Madurez" style={{ height: '100%' }}>
                      <MaturityChart data={maturityLevel} permits={{isSuperuser, hasAnyRole, canEvaluationRead}} />
                    </Card>
                  </Skeleton>
                </div>
              </Col>
              <Col xs={24} md={16}>
                <Skeleton loading={loading} paragraph={{ rows: 3, width: '100%' }} active>
                  <Card className="custom-card-title" title="Estado de Controles" style={{ height: '100%' }}>
                    <EvaluationsChart 
                      data={evaluations} 
                      permits={{isSuperuser, hasAnyRole, canEvaluationRead}}
                      appliedFilters={appliedFilters}
                      assets={assets}  
                    />
                  </Card>
                </Skeleton>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Tercera fila: 3 cards para gráficos - ALTURA UNIFORME */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Row gutter={[16, 16]} align="stretch">
              {/* Columna de GAPs con dos Cards apiladas */}
              <Col xs={24} md={8}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Skeleton loading={loading} paragraph={{ rows: 3, width: '100%' }} active>
                    <Card 
                      className="custom-card-title" 
                      title="Estado de GAPs" 
                      style={{ flex: 1, marginBottom: 8, display: 'flex', flexDirection: 'column' }}
                      bodyStyle={{ flex: 1, padding: '16px', minHeight: 0 }}
                    >
                      <div style={{ height: '100%', width: '100%' }}>
                        <GapStatusChart 
                          data={gaps} 
                          permits={{isSuperuser, hasAnyRole, canGapsRead}}
                          appliedFilters={appliedFilters}
                          assets={assets}  
                        />
                      </div>
                    </Card>
                  
                    <Card 
                      bodyStyle={{ padding: '12px', fontSize: '13px', color: '#555' }}
                    >
                      <Alert 
                        message={`GAPs Vencidos: ${(isSuperuser || (hasAnyRole && canGapsRead)) ? gaps?.defeated || 0 : '-'}`} 
                        type="error" 
                        showIcon
                        onClick={() => {
                          if (!(isSuperuser || (hasAnyRole && canGapsRead))) return;
                          
                          // ✅ Construir URL con filtros
                          const params = new URLSearchParams();
                          params.set('defeated', 'true');
                          
                          // Agregar filtros de activos
                          if (appliedFilters.asset && appliedFilters.asset.length > 0) {
                            const assetNames = appliedFilters.asset
                              .map(code => assets.find(a => a.code === code)?.name)
                              .filter(Boolean);
                            
                            if (assetNames.length > 0) {
                              params.append('assets', assetNames.join(','));
                            }
                          }
                          
                          // Agregar filtros de áreas
                          if (appliedFilters.area && appliedFilters.area.length > 0) {
                            const allFilterAreas = assets.reduce((acc, asset) => {
                              if (asset.filter_areas) {
                                asset.filter_areas.forEach(area => {
                                  if (!acc.find(a => a.id === area.id)) {
                                    acc.push(area);
                                  }
                                });
                              }
                              return acc;
                            }, []);

                            const areaNames = appliedFilters.area
                              .map(id => allFilterAreas.find(a => a.id === id)?.name)
                              .filter(Boolean);
                            
                            if (areaNames.length > 0) {
                              params.append('areas', areaNames.join(','));
                            }
                          }
                          
                          navigate(`/gaps?${params.toString()}`);
                        }}
                        style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: (isSuperuser || (hasAnyRole && canGapsRead)) ? '#cf1322' : 'black',
                          backgroundColor: (isSuperuser || (hasAnyRole && canGapsRead)) ? '' : '#f5f5f5',
                          opacity: (isSuperuser || (hasAnyRole && canGapsRead)) ? 1 : 0.5,
                          cursor: (isSuperuser || (hasAnyRole && canGapsRead)) ? 'pointer' : 'not-allowed'
                        }}
                      />
                    </Card>
                  </Skeleton>
                </div>
              </Col>

              {/* Columna de Estado de Tareas - Altura completa */}
              <Col xs={24} md={8}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Skeleton loading={loading} paragraph={{ rows: 6, width: '100%' }} active>
                    <Card 
                      className="custom-card-title" 
                      title="Estado de Tareas"
                      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                      bodyStyle={{ flex: 1, padding: '16px', minHeight: 0 }}
                    >
                      <div style={{ height: '100%', width: '100%' }}>
                        <TasksStatusChart 
                          data={tasks} 
                          permits={{isSuperuser, hasAnyRole, canTasksRead}}
                          appliedFilters={appliedFilters}
                          assets={assets}  
                        />
                      </div>
                    </Card>
                  </Skeleton>
                </div>
              </Col>

              {/* Columna de Condición de Tareas - Altura completa */}
              <Col xs={24} md={8}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Skeleton loading={loading} paragraph={{ rows: 6, width: '100%' }} active>
                    <Card 
                      className="custom-card-title" 
                      title="Condición de Tareas"
                      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                      bodyStyle={{ flex: 1, padding: '16px', minHeight: 0 }}
                    >
                      <div style={{ height: '100%', width: '100%' }}>
                        <ConditionTasksChart 
                          data={tasks} 
                          permits={{isSuperuser, hasAnyRole, canTasksRead}}
                          appliedFilters={appliedFilters}
                          assets={assets}  
                         />
                      </div>
                    </Card>
                  </Skeleton>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Cuarta fila: 2 cards para gráficos */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <div style={{ height: '100%' }}>
                  <Skeleton loading={loading} paragraph={{ rows: 3, width: '100%' }} active>
                    <Card className="custom-card-title" title="Controles por Prioridad de Implementación" style={{ height: '100%' }}>
                      <ControlsByPriorityChart 
                        data={groupControlsBy(controls, 'priority')} 
                        permits={{isSuperuser, hasAnyRole, canControlsRead}}
                        appliedFilters={appliedFilters}
                        assets={assets}  
                      />
                    </Card>
                  </Skeleton>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div style={{ height: '100%' }}>
                  <Skeleton loading={loading} paragraph={{ rows: 3, width: '100%' }} active>
                    <Card className="custom-card-title" title="Controles por Tipo" style={{ height: '100%' }}>
                      <ControlsByTypeChart 
                        data={groupControlsBy(controls, 'type')} 
                        permits={{isSuperuser, hasAnyRole, canControlsRead}}
                        appliedFilters={appliedFilters}
                        assets={assets}
                      />
                    </Card>
                  </Skeleton>
                </div>
              </Col>
            </Row>
          </Col>  
        </Row>
      </div>
    </WithPermission>
  );
}

export default Dashboard